import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Fetchexecutequery,
  Fetchupdatequery,
} from "../../../services/process/BRS1/brsService7";
import { headers, baseUrl } from "../../../services/common/credentials";
import { CreateData } from "../../../services/process/process_data/process_data";
import {
  Calendar, MapPin, ClipboardList, Search, Star, Users,
  ChevronLeft, ChevronRight, Check, Save, Upload, AlertCircle,
  CheckCircle, Zap, Building, Plus, Edit3, Trash2, Eye,
  List, AlertTriangle, X, FileText, BarChart2, Copy,
  Archive, MoreVertical, Globe, Link2, Clock,
} from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────────────────
const getUserCredentials = () =>
  JSON.parse(sessionStorage.getItem("userCredentials") || "{}");

const getFormattedDate = () =>
  new Date().toISOString().slice(0, 19).replace("T", " ");

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  navy:        "#0a2352",
  navyDark:    "#061535",
  navyMid:     "#143272",
  orange:      "#f97316",
  orangeHover: "#ea6c0a",
  bg:          "#eef3f9",
  card:        "#ffffff",
  border:      "#d4dce8",
  text:        "#0f172a",
  muted:       "#64748b",
  light:       "#e8eef5",
  success:     "#16a34a",
  danger:      "#dc2626",
  warn:        "#d97706",
  purple:      "#7c3aed",
  accent:      "#2563eb",
};

// ── File upload config ────────────────────────────────────────────────────────
const STATIC_PATH   = "/home/demo/UserManagementModule/static/reference_files/";
const UPLOAD_URL    = `${baseUrl}/api/v2/user_modules/upload/`;
const DOWNLOAD_BASE = `${baseUrl}/api/v2/user_modules/serve-pdf/`;

// ── Option lists ──────────────────────────────────────────────────────────────
const EVENT_CATEGORY_OPTIONS = [
  "Career Fair","University Fair","Cultural Event","STEM Event","Sports Event","Workshop","Seminar",
"Competition", "Other"];

const EVENT_MODE_OPTIONS = ["Offline", "Online", "Hybrid"];

const TARGET_AUDIENCE_OPTIONS = [
  "Undergraduate Students", "Postgraduate Students", "Faculty", "Researchers",
  "Industry Professionals", "School Students", "Mixed Audience",
];

const YES_NO_OPTIONS = ["Yes", "No"];

// ── Step definitions ──────────────────────────────────────────────────────────
const STEPS = [
  { label: "Basic Info",          icon: ClipboardList, fields: ["event_name","event_category","event_mode","description","event_start_date","event_end_date"] },
  { label: "Location & Audience", icon: MapPin,         fields: ["country","city","venue","target_audience"] },
  { label: "Registration",        icon: Calendar,       fields: ["reg_open_date","reg_open_time","reg_close_date","reg_close_time"] },
  { label: "Review Setup",        icon: Search,         fields: ["review_start_date","review_end_date"] },
  { label: "Invitation Setup",    icon: Star,           fields: ["invitation_release_date","acceptance_deadline"] },
  { label: "Agenda",              icon: Clock,          fields: [] },
  { label: "Participation Rules", icon: Users,          fields: [] },
];

// ── ALL required fields including agenda ─────────────────────────────────────
// Agenda is treated separately (must have ≥1 valid item)
const ALL_REQUIRED_FIELDS = STEPS.flatMap(s => s.fields);

// ── Date-order validation pairs ───────────────────────────────────────────────
const DATE_ORDER_PAIRS = {
  0: [["event_start_date", "event_end_date", "Event"]],
  2: [["reg_open_date", "reg_close_date", "Registration"]],
  3: [["review_start_date", "review_end_date", "Review"]],
  4: [["invitation_release_date", "acceptance_deadline", "Invitation acceptance"]],
};

const checkDateOrder = (stepIdx, fd) => {
  const pairs = DATE_ORDER_PAIRS[stepIdx];
  if (!pairs) return null;
  for (const [startKey, endKey, label] of pairs) {
    if (fd[startKey] && fd[endKey] && fd[endKey] < fd[startKey]) {
      return `${label} end date must be the same as or later than the start date.`;
    }
  }
  return null;
};

// ── Agenda item validation ─────────────────────────────────────────────────────
const isAgendaItemValid = (item, fd) => {
  if (!item.item_date || !item.start_time || !item.end_time) return false;
  if (fd.event_start_date && item.item_date < fd.event_start_date) return false;
  if (fd.event_end_date && item.item_date > fd.event_end_date) return false;
  if (item.start_time >= item.end_time) return false;
  return true;
};

// ── Completion: fields + agenda ───────────────────────────────────────────────
// Total slots = required fields + 1 (for agenda having ≥1 valid session)
const TOTAL_COMPLETION_SLOTS = ALL_REQUIRED_FIELDS.length + 1;

const calcCompletion = (fd) => {
  const filledFields = ALL_REQUIRED_FIELDS.filter(f => fd[f]).length;
  const agendaComplete = Array.isArray(fd.agendaItems)
    && fd.agendaItems.length > 0
    && fd.agendaItems.every(it => isAgendaItemValid(it, fd))
    ? 1 : 0;
  return Math.round(((filledFields + agendaComplete) / TOTAL_COMPLETION_SLOTS) * 100);
};

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_META = {
  draft:                  { label: "Draft",                bg: "#f3f4f6", color: "#6b7280" },
  published:              { label: "Published",            bg: "#dbeafe", color: "#1d4ed8" },
  registration_open:      { label: "Reg. Open",            bg: "#d1fae5", color: "#065f46" },
  registration_closed:    { label: "Reg. Closed",          bg: "#fef3c7", color: "#92400e" },
  under_review:           { label: "Under Review",         bg: "#fef3c7", color: "#92400e" },
  invitations_released:   { label: "Invitations Released", bg: "#ede9fe", color: "#5b21b6" },
  completed:              { label: "Completed",            bg: "#d1fae5", color: "#065f46" },
  archived:               { label: "Archived",             bg: "#f3f4f6", color: "#6b7280" },
};

const statusBadge = (status) => {
  const m = STATUS_META[status] || { label: status, bg: "#f3f4f6", color: "#6b7280" };
  return (
    <span style={{
      background: m.bg, color: m.color,
      padding: "3px 12px", borderRadius: 99,
      fontSize: 13, fontWeight: 700, letterSpacing: .4,
      textTransform: "uppercase", whiteSpace: "nowrap",
    }}>
      {m.label}
    </span>
  );
};

const stepComplete = (stepIdx, fd) => {
  if (STEPS[stepIdx].label === "Agenda") {
    return Array.isArray(fd.agendaItems) && fd.agendaItems.length > 0
      && fd.agendaItems.every(it => isAgendaItemValid(it, fd));
  }
  return STEPS[stepIdx].fields.every(f => fd[f]);
};

const lastUnlockedStep = (fd) => {
  let last = 0;
  for (let i = 0; i < STEPS.length - 1; i++) {
    if (stepComplete(i, fd)) last = i + 1;
    else break;
  }
  return last;
};

// ── Shared components ─────────────────────────────────────────────────────────

const Card = ({ children, style = {} }) => (
  <div style={{
    background: T.card, borderRadius: 14,
    border: `1px solid ${T.border}`,
    boxShadow: "0 4px 20px rgba(10,35,82,.07)",
    overflow: "hidden",
    ...style,
  }}>
    {children}
  </div>
);

const StatCard = ({ label, value, icon: Icon, color, sub }) => (
  <Card style={{ padding: "18px 20px", flex: 1, minWidth: 140, overflow: "visible" }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <div style={{ color: T.muted, fontSize: 14, fontWeight: 700, letterSpacing: .8, textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: 30, fontWeight: 900, color: T.text, lineHeight: 1.1, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 15, color: T.muted, marginTop: 3 }}>{sub}</div>}
      </div>
      <div style={{ background: color + "18", borderRadius: 12, padding: 10 }}>
        <Icon size={22} color={color} />
      </div>
    </div>
  </Card>
);

const Field = ({ label, required, children, hint }) => (
  <div style={{ marginBottom: 18 }}>
    <label style={{
      display: "block", fontSize: 15, fontWeight: 700,
      color: T.navy, marginBottom: 6, letterSpacing: .3, textTransform: "uppercase",
    }}>
      {label}{required && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
    </label>
    {children}
    {hint && <p style={{ fontSize: 13, color: T.muted, margin: "5px 0 0" }}>{hint}</p>}
  </div>
);

const inputBase = {
  width: "100%", padding: "10px 14px", borderRadius: 8,
  border: `1.5px solid ${T.border}`, fontSize: 16, color: T.text,
  background: "#fff", outline: "none", boxSizing: "border-box",
  transition: "border .18s, box-shadow .18s",
};

const Input = ({ style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input {...props}
      style={{ ...inputBase, borderColor: focused ? T.orange : T.border, boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none", ...style }}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    />
  );
};

const SelectInput = ({ children, style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <select {...props}
      style={{ ...inputBase, borderColor: focused ? T.orange : T.border, boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none", ...style }}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    >
      {children}
    </select>
  );
};

const TextareaInput = ({ style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <textarea {...props}
      style={{ ...inputBase, borderColor: focused ? T.orange : T.border, boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none", resize: "vertical", minHeight: 90, ...style }}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    />
  );
};

const Btn = ({ children, variant = "primary", size = "md", icon: Icon, onClick, disabled, style = {} }) => {
  const [hovered, setHovered] = useState(false);
  const pad = size === "sm" ? "7px 15px" : "11px 24px";
  const fs  = size === "sm" ? 15 : 17;
  const variants = {
    primary:   { background: hovered ? T.orangeHover : T.orange,  color: "#fff",  border: "none" },
    secondary: { background: hovered ? T.light : "#f1f5f9",        color: T.navy,  border: `1.5px solid ${T.border}` },
    outline:   { background: "transparent", color: hovered ? T.orange : T.navy, border: `1.5px solid ${hovered ? T.orange : T.border}` },
    success:   { background: hovered ? "#15803d" : T.success,      color: "#fff",  border: "none" },
    danger:    { background: hovered ? "#b91c1c" : T.danger,       color: "#fff",  border: "none" },
    navy:      { background: hovered ? T.navyMid : T.navy,         color: "#fff",  border: "none" },
  };
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        borderRadius: 8, fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        padding: pad, fontSize: fs,
        transition: "all .15s", opacity: disabled ? .6 : 1,
        ...variants[variant], ...style,
      }}
    >
      {Icon && <Icon size={size === "sm" ? 14 : 16} />}
      {children}
    </button>
  );
};

const InfoBox = ({ children }) => (
  <div style={{
    display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20,
    padding: "12px 16px", background: "rgba(10,35,82,.05)",
    borderRadius: 8, border: `1px solid ${T.border}`,
  }}>
    <AlertCircle size={16} color={T.navy} style={{ flexShrink: 0, marginTop: 2 }} />
    <span style={{ fontSize: 16, color: T.muted, lineHeight: 1.6 }}>{children}</span>
  </div>
);

const PillToggle = ({ selected, onClick, children }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        padding: "7px 15px", borderRadius: 99, fontSize: 15, fontWeight: 700,
        cursor: "pointer", transition: "all .15s",
        border: `1.5px solid ${selected ? T.orange : (hovered ? T.navy : T.border)}`,
        background: selected ? "rgba(249,115,22,.12)" : "#fff",
        color: selected ? T.orange : T.muted,
      }}
    >
      {children}
    </button>
  );
};

const ConfirmDialog = ({ message, onConfirm, onCancel }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(6,21,53,.55)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 420, width: "90%", boxShadow: "0 20px 60px rgba(6,21,53,.25)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <AlertTriangle size={22} color={T.danger} />
        <span style={{ fontWeight: 800, fontSize: 16, color: T.text }}>Confirm Delete</span>
      </div>
      <p style={{ fontSize: 14, color: T.muted, margin: "0 0 20px", lineHeight: 1.6 }}>{message}</p>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
        <Btn variant="danger" icon={Trash2} onClick={onConfirm}>Delete</Btn>
      </div>
    </div>
  </div>
);

const ViewModal = ({ event, onClose }) => {
  if (!event) return null;
  let agendaCount = 0;
  try {
    const parsed = typeof event.event_agenda === "string" ? JSON.parse(event.event_agenda) : event.event_agenda;
    agendaCount = Array.isArray(parsed) ? parsed.length : 0;
  } catch { agendaCount = 0; }

  const rows = [
    ["Event Name",        event.event_name],
    ["Category",          event.event_category],
    ["Event Mode",        event.event_mode],
    ["Industry",          event.industry],
    ["Event From",        event.event_start_date],
    ["Event To",          event.event_end_date],
    ["Country",           event.country],
    ["City",              event.city],
    ["Venue",             event.venue],
    ["Map / Meeting Link", event.venue_link],
    ["Target Audience",   event.target_audience],
    ["Reg. Open",         `${event.reg_open_date || "—"} ${event.reg_open_time || ""}`],
    ["Reg. Close",        `${event.reg_close_date || "—"} ${event.reg_close_time || ""}`],
    ["Review Period",     `${event.review_start_date || "—"} → ${event.review_end_date || "—"}`],
    ["Invitation Date",   event.invitation_release_date],
    ["Acceptance By",     event.acceptance_deadline],
    ["Agenda Sessions",   agendaCount ? `${agendaCount} session(s)` : "—"],
    ["International Collaboration", event.international_collaboration],
    ["Intl. Universities Invited",  event.international_universities_invited],
    ["Expected Participants", event.expected_participants],
    ["Max Applicants",    event.max_applicants || "Unlimited"],
    ["Max Invitations",   event.max_invitations || "—"],
    ["Status",            event.event_status],
  ];
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(6,21,53,.55)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 660, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(6,21,53,.25)" }}>
        <div style={{ padding: "16px 24px", background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>Event Details</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer" }}><X size={18} /></button>
        </div>
        <div style={{ padding: 24 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <tbody>
              {rows.map(([label, value]) => (
                <tr key={label} style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={{ padding: "10px 12px", fontWeight: 700, color: T.navy, width: "40%", textTransform: "uppercase", fontSize: 12, letterSpacing: .3 }}>{label}</td>
                  <td style={{ padding: "10px 12px", color: T.text }}>{value || "—"}</td>
                </tr>
              ))}
              {event.key_highlights && (
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={{ padding: "10px 12px", fontWeight: 700, color: T.navy, textTransform: "uppercase", fontSize: 12, letterSpacing: .3, verticalAlign: "top" }}>Key Highlights</td>
                  <td style={{ padding: "10px 12px", color: T.text, lineHeight: 1.6 }}>{event.key_highlights}</td>
                </tr>
              )}
              {event.description && (
                <tr>
                  <td style={{ padding: "10px 12px", fontWeight: 700, color: T.navy, textTransform: "uppercase", fontSize: 12, letterSpacing: .3, verticalAlign: "top" }}>Description</td>
                  <td style={{ padding: "10px 12px", color: T.text, lineHeight: 1.6 }}>{event.description}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ActionMenu = ({ ev, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button onClick={() => setOpen(p => !p)} style={{ background: T.light, border: "none", borderRadius: 6, padding: "5px 8px", cursor: "pointer" }}>
        <MoreVertical size={15} color={T.muted} />
      </button>
      {open && (
        <div style={{ position: "absolute", right: 0, top: "100%", background: "#fff", border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,.12)", zIndex: 200, minWidth: 180, padding: 6 }}>
          {[
            { label: "Edit Event",      icon: Edit3,    action: () => { onEdit(ev.primary_id); setOpen(false); } },
            { label: "View Details",    icon: Eye,      action: () => setOpen(false) },
            { label: "Clone Event",     icon: Copy,     action: () => setOpen(false) },
            { label: "View Analytics",  icon: BarChart2, action: () => setOpen(false) },
            { label: "Archive",         icon: Archive,  action: () => setOpen(false), danger: true },
            { label: "Delete",          icon: Trash2,   action: () => { onDelete(ev); setOpen(false); }, danger: true },
          ].map(item => (
            <button key={item.label} onClick={item.action}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 14px", background: "none", border: "none", cursor: "pointer", borderRadius: 6, color: item.danger ? T.danger : T.text, fontSize: 14, fontWeight: 600, textAlign: "left" }}>
              <item.icon size={14} /> {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
export function EventManagement() {
  const uc = getUserCredentials();
  const editIdRef = useRef(null);

  const [view, setView]               = useState("list");
  const [events, setEvents]           = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [searchText, setSearchText]   = useState("");
  const [viewEvent, setViewEvent]     = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [editId, setEditId]         = useState(null);
  const [step, setStep]             = useState(0);
  const [saving, setSaving]         = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [stepError, setStepError]   = useState("");

  const emptyForm = {
    event_name: "", event_category: "", description: "", industry: "",
    event_mode: "", target_audience: "", venue_link: "", key_highlights: "",
    country: "", city: "", venue: "",
    reg_open_date: "", reg_open_time: "", reg_close_date: "", reg_close_time: "",
    review_start_date: "", review_end_date: "",
    invitation_release_date: "", acceptance_deadline: "",
    event_start_date: "", event_end_date: "",
    max_applicants: "", max_invitations: "", expected_participants: "",
    international_collaboration: "", international_universities_invited: "",
    eligibility_criteria: "",
    banner_file_name: "", brochure_file_name: "",
    agendaItems: [],
    event_status: "draft",
  };

  const [formData, setFormData] = useState({ ...emptyForm });

  const [bannerUploaded, setBannerUploaded]   = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const bannerRef = useRef(null);

  const [brochureUploaded, setBrochureUploaded]   = useState(null);
  const [brochureUploading, setBrochureUploading] = useState(false);
  const brochureRef = useRef(null);

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Banner too large. Max 5 MB."); e.target.value = ""; return; }
    setBannerUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("filePath", STATIC_PATH);
    try {
      const res  = await fetch(UPLOAD_URL, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        setBannerUploaded({ fileName: data.Filename, filePath: data.Filepath });
        setFormData(p => ({ ...p, banner_file_name: data.Filename }));
      } else {
        const err = await res.json();
        alert("Upload failed: " + (err.Message || "Unknown error"));
      }
    } catch { alert("Upload failed. Please try again."); }
    finally { setBannerUploading(false); if (bannerRef.current) bannerRef.current.value = ""; }
  };

  const clearBanner = () => {
    setBannerUploaded(null);
    setFormData(p => ({ ...p, banner_file_name: "" }));
    if (bannerRef.current) bannerRef.current.value = "";
  };

  const handleBrochureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      alert("Brochure must be a PDF file."); e.target.value = ""; return;
    }
    if (file.size > 5 * 1024 * 1024) { alert("Brochure too large. Max 5 MB."); e.target.value = ""; return; }
    setBrochureUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("filePath", STATIC_PATH);
    try {
      const res  = await fetch(UPLOAD_URL, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        setBrochureUploaded({ fileName: data.Filename, filePath: data.Filepath });
        setFormData(p => ({ ...p, brochure_file_name: data.Filename }));
      } else {
        const err = await res.json();
        alert("Upload failed: " + (err.Message || "Unknown error"));
      }
    } catch { alert("Upload failed. Please try again."); }
    finally { setBrochureUploading(false); if (brochureRef.current) brochureRef.current.value = ""; }
  };

  const clearBrochure = () => {
    setBrochureUploaded(null);
    setFormData(p => ({ ...p, brochure_file_name: "" }));
    if (brochureRef.current) brochureRef.current.value = "";
  };

  // ── Load events ───────────────────────────────────────────────────────────
  const fetchEvents = async () => {
    setListLoading(true);
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT primary_id, event_name, event_category, event_mode, industry, country, city,
                       event_start_date, event_end_date, event_status, reg_open_date, reg_close_date,
                       max_applicants, max_invitations, banner_file_name,
                       created_date, modified_date
                FROM edu_platform.events
                WHERE is_active = 1
                  AND creator_entity_id = ${uc.selectedentity || uc.entities_id} 
                  AND tenant_id = ${uc.tenants_id} AND division_id = ${uc.division[0]}
                ORDER BY primary_id DESC;`,
      }, creds[0]);
      setEvents(res.data || []);
    } catch (err) { console.error("Error fetching events:", err); }
    finally { setListLoading(false); }
  };

  useEffect(() => { fetchEvents(); }, []);

  const totalEvents    = events.length;
  const activeEvents   = events.filter(e => e.event_status === "registration_open").length;
  const draftEvents    = events.filter(e => e.event_status === "draft").length;
  const publishedCount = events.filter(e => !["draft","archived"].includes(e.event_status)).length;

  // ── AG Grid columns ───────────────────────────────────────────────────────
  const columnDefs = [
    {
      headerName: "Event Name", field: "event_name", flex: 2, minWidth: 200,
      cellRenderer: (p) => <span style={{ fontWeight: 700, color: T.navy, fontSize: 14 }}>{p.value}</span>,
    },
    { headerName: "Category", field: "event_category", width: 140 },
    { headerName: "Mode",     field: "event_mode",      width: 100 },
    { headerName: "Country",  field: "country",         width: 115 },
    { headerName: "City",     field: "city",            width: 115 },
    {
      headerName: "Event Dates", field: "event_start_date", width: 210,
      cellRenderer: p => {
        const from = p.data.event_start_date ? new Date(p.data.event_start_date).toLocaleDateString("en-GB") : "—";
        const to   = p.data.event_end_date   ? new Date(p.data.event_end_date).toLocaleDateString("en-GB")   : "—";
        return from === to ? from : `${from} → ${to}`;
      },
    },
    {
      headerName: "Status", field: "event_status", width: 170,
      cellRenderer: p => statusBadge(p.value),
    },
    {
      headerName: "Actions", width: 160, sortable: false, filter: false,
      cellRenderer: (p) => (
        <div style={{ display: "flex", gap: 6, alignItems: "center", height: "100%" }}>
          <button title="View" onClick={() => setViewEvent(p.data)}
            style={{ background: "rgba(10,35,82,.08)", border: "none", borderRadius: 6, padding: "5px 9px", cursor: "pointer", color: T.navy, display: "flex", alignItems: "center" }}>
            <Eye size={14} />
          </button>
          <button title="Edit" onClick={() => openEdit(p.data.primary_id)}
            style={{ background: "rgba(249,115,22,.1)", border: "none", borderRadius: 6, padding: "5px 9px", cursor: "pointer", color: T.orange, display: "flex", alignItems: "center" }}>
            <Edit3 size={14} />
          </button>
          <button title="Delete" onClick={() => setDeleteTarget({ primary_id: p.data.primary_id, event_name: p.data.event_name })}
            style={{ background: "rgba(220,38,38,.1)", border: "none", borderRadius: 6, padding: "5px 9px", cursor: "pointer", color: T.danger, display: "flex", alignItems: "center" }}>
            <Trash2 size={14} />
          </button>
        </div>
      ),
    },
  ];

  // ── Open edit ─────────────────────────────────────────────────────────────
  const openEdit = async (primaryId) => {
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT * FROM edu_platform.events WHERE primary_id = ${primaryId} AND is_active = 1 LIMIT 1;`,
      }, creds[0]);
      if (res.data && res.data.length > 0) {
        const row = res.data[0];
        const fd = {};
        Object.keys(emptyForm).forEach(k => {
          if (k === "agendaItems") return;
          fd[k] = row[k] ?? "";
        });
        try {
          fd.agendaItems = row.event_agenda
            ? (typeof row.event_agenda === "string" ? JSON.parse(row.event_agenda) : row.event_agenda)
            : [];
          if (!Array.isArray(fd.agendaItems)) fd.agendaItems = [];
        } catch { fd.agendaItems = []; }

        setFormData(fd);
        if (row.banner_file_name) setBannerUploaded({ fileName: row.banner_file_name, filePath: "" });
        if (row.brochure_file_name) setBrochureUploaded({ fileName: row.brochure_file_name, filePath: "" });
        setStep(0);
        setStepError("");
        setSaveStatus(null);
        setView("form");
        editIdRef.current = primaryId;
        setEditId(primaryId);
      }
    } catch (err) { console.error("Error loading event:", err); }
  };

  const openCreate = () => {
    setFormData({ ...emptyForm, agendaItems: [] });
    setBannerUploaded(null);
    setBrochureUploaded(null);
    setStep(0);
    setStepError("");
    setSaveStatus(null);
    setView("form");
    editIdRef.current = null;
    setEditId(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const creds = Fetchupdatequery();
      await axios.post(creds[1], {
        schema_name: "edu_platform",
        table: "events",
        set_column: [{ is_active: 0, modified_date: getFormattedDate(), modified_by: uc.user_id }],
        where: [{ primary_id: deleteTarget.primary_id }],
      }, creds[0]);
      setDeleteTarget(null);
      fetchEvents();
    } catch (err) { console.error("Delete error:", err); }
  };

  const handle = (k, v) => {
    setFormData(p => {
      const next = { ...p, [k]: v };
      if (k === "event_start_date") {
        if (!p.event_end_date || p.event_end_date === p.event_start_date) {
          next.event_end_date = v;
        }
      }
      return next;
    });
    setStepError("");
  };

  const toggleTargetAudience = (option) => {
    setFormData(p => {
      const current = p.target_audience ? p.target_audience.split(",").map(s => s.trim()).filter(Boolean) : [];
      const next = current.includes(option) ? current.filter(o => o !== option) : [...current, option];
      return { ...p, target_audience: next.join(",") };
    });
    setStepError("");
  };

  // ── Agenda item handlers ──────────────────────────────────────────────────
  const addAgendaItem = () => {
    setFormData(p => ({
      ...p,
      agendaItems: [
        ...(p.agendaItems || []),
        {
          id: `agenda_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          item_date: p.event_start_date || "",
          start_time: "",
          end_time: "",
          title: "",
          description: "",
        },
      ],
    }));
    setStepError("");
  };

  const updateAgendaItem = (id, key, value) => {
    setFormData(p => ({
      ...p,
      agendaItems: (p.agendaItems || []).map(it => it.id === id ? { ...it, [key]: value } : it),
    }));
    setStepError("");
  };

  const removeAgendaItem = (id) => {
    setFormData(p => ({ ...p, agendaItems: (p.agendaItems || []).filter(it => it.id !== id) }));
    setStepError("");
  };

  // ── Step navigation ───────────────────────────────────────────────────────
  const tryGoToStep = (targetIdx) => {
    if (targetIdx < 0 || targetIdx >= STEPS.length) return;
    if (targetIdx < step) { setStep(targetIdx); setStepError(""); return; }
    for (let i = step; i < targetIdx; i++) {
      const dateErr = checkDateOrder(i, formData);
      if (dateErr) { setStepError(dateErr); return; }
      if (!stepComplete(i, formData)) {
        if (STEPS[i].label === "Agenda") {
          setStepError(`Complete Step ${i + 1} (Agenda) first — add at least one valid agenda session with a date inside the event dates and an end time after the start time.`);
        } else {
          const missing = STEPS[i].fields.filter(f => !formData[f]);
          setStepError(`Complete Step ${i + 1} (${STEPS[i].label}) first. Missing: ${missing.map(f => f.replace(/_/g, " ")).join(", ")}.`);
        }
        return;
      }
    }
    setStep(targetIdx);
    setStepError("");
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async (status) => {
    if (status !== "draft") {
      const missing = ALL_REQUIRED_FIELDS.filter(f => !formData[f]);
      if (missing.length > 0) {
        setStepError(`Cannot publish — fill all required fields first. Missing: ${missing.map(f => f.replace(/_/g, " ")).join(", ")}.`);
        return;
      }
      for (let i = 0; i < STEPS.length; i++) {
        const dateErr = checkDateOrder(i, formData);
        if (dateErr) { setStepError(dateErr); return; }
      }
      if (!Array.isArray(formData.agendaItems) || formData.agendaItems.length === 0
          || !formData.agendaItems.every(it => isAgendaItemValid(it, formData))) {
        setStepError("Cannot publish — add at least one valid agenda session (date within event dates, end time after start time).");
        return;
      }
    }

    setSaving(true);
    setSaveStatus(null);
    const now = getFormattedDate();
    const isDraft = status === "draft";

    const payload = {
      event_name:              formData.event_name              || (isDraft ? "Untitled Draft" : ""),
      event_category:          formData.event_category          || (isDraft ? "Conference" : ""),
      event_mode:              formData.event_mode              || (isDraft ? " " : ""),
      description:             formData.description             || (isDraft ? " " : ""),
      industry:                formData.industry                || null,
      target_audience:         formData.target_audience         || null,
      venue_link:              formData.venue_link              || null,
      key_highlights:          formData.key_highlights          || null,
      country:                 formData.country                 || (isDraft ? " " : ""),
      city:                    formData.city                    || (isDraft ? " " : ""),
      venue:                   formData.venue                   || (isDraft ? " " : ""),
      reg_open_date:           formData.reg_open_date           || null,
      reg_open_time:           formData.reg_open_time           || null,
      reg_close_date:          formData.reg_close_date          || null,
      reg_close_time:          formData.reg_close_time          || null,
      review_start_date:       formData.review_start_date       || null,
      review_end_date:         formData.review_end_date         || null,
      invitation_release_date: formData.invitation_release_date || null,
      acceptance_deadline:     formData.acceptance_deadline     || null,
      event_start_date:         formData.event_start_date         || null,
      event_end_date:           formData.event_end_date           || null,
      event_agenda:            JSON.stringify(formData.agendaItems || []),
      max_applicants:          formData.max_applicants          || 0,
      max_invitations:         formData.max_invitations         || 0,
      expected_participants:   formData.expected_participants   || 0,
      international_collaboration:            formData.international_collaboration            || null,
      international_universities_invited:     formData.international_universities_invited     || null,
      eligibility_criteria:    formData.eligibility_criteria    || null,
      banner_file_name:        formData.banner_file_name        || null,
      brochure_file_name:      formData.brochure_file_name      || null,
      event_status:            status,
      modified_date:           now,
    };

    try {
      if (editIdRef.current) {
        const creds = Fetchupdatequery();
        await axios.post(creds[1], {
          schema_name: "edu_platform",
          table: "events",
          set_column: [{ ...payload, modified_by: uc.user_id }],
          where: [{ primary_id: editIdRef.current }],
        }, creds[0]);
      } else {
        const creds = CreateData();
        const res = await axios.post(creds[1], {
          database: "edu_platform",
          table: "events",
          formData: {
            ...payload,
            creator_user_id:   uc.user_id,
            creator_entity_id: uc.selectedentity || uc.entities_id,
            tenant_id:         uc.tenants_id,
            division_id: uc.division_id[0],
            group_id:          uc.groups_id,
            created_by:        uc.user_id,
            created_date:      now,
            is_active:         1,
          },
        }, creds[0]);

        if (res?.data?.primary_id) {
          editIdRef.current = res.data.primary_id;
          setEditId(res.data.primary_id);
        }
      }

      setSaveStatus("saved");
      setFormData(p => ({ ...p, event_status: status }));
      setTimeout(() => setSaveStatus(null), 3500);
    } catch (err) {
      console.error("Save error:", err);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  const goBackToList = () => { setView("list"); fetchEvents(); };

  // ── Step content ──────────────────────────────────────────────────────────
  const stepContent = [
    // Step 0 — Basic Info
    <div key="s0">
      <Field label="Event Name" required>
        <Input value={formData.event_name} onChange={e => handle("event_name", e.target.value)} placeholder="e.g. Global EdTech Summit 2025" />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Event Category" required>
          <SelectInput value={formData.event_category} onChange={e => handle("event_category", e.target.value)}>
            <option value="">Select category</option>
            {EVENT_CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </SelectInput>
        </Field>
        <Field label="Event Mode" required>
          <SelectInput value={formData.event_mode} onChange={e => handle("event_mode", e.target.value)}>
            <option value="">Select mode</option>
            {EVENT_MODE_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
          </SelectInput>
        </Field>
      </div>

      <Field label="Industry">
        <SelectInput value={formData.industry} onChange={e => handle("industry", e.target.value)}>
          <option value="">Select industry</option>
          {["Education","Technology","Science","Arts","Leadership","Innovation"].map(c => <option key={c} value={c}>{c}</option>)}
        </SelectInput>
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Event Start Date" required hint="Selecting this auto-fills the end date">
          <Input type="date" value={formData.event_start_date} onChange={e => handle("event_start_date", e.target.value)} />
        </Field>
        <Field label="Event End Date" required hint="Must be the same as or after the start date">
          <Input type="date" value={formData.event_end_date} min={formData.event_start_date || undefined} onChange={e => handle("event_end_date", e.target.value)} />
        </Field>
      </div>

      <Field label="Event Description" required>
        <TextareaInput value={formData.description} onChange={e => handle("description", e.target.value)} placeholder="Describe your event — goals, audience, agenda…" />
      </Field>

      <Field label="Key Highlights / Themes" hint="Optional — list the key themes or highlights of the event">
        <TextareaInput value={formData.key_highlights} onChange={e => handle("key_highlights", e.target.value)} placeholder="e.g. AI in Education, Sustainability, Industry-Academia Collaboration" style={{ minHeight: 70 }} />
      </Field>

      {/* Banner upload */}
      <Field label="Event Banner" hint="PNG or JPG · max 5 MB · Recommended 1200×400 px">
        <input ref={bannerRef} type="file" accept=".png,.jpg,.jpeg" style={{ display: "none" }} onChange={handleBannerUpload} />
        {bannerUploaded ? (
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", background: "rgba(249,115,22,.05)", borderRadius: 10, border: `1.5px dashed ${T.orange}` }}>
            <div style={{ background: "rgba(249,115,22,.15)", borderRadius: 8, padding: 10, flexShrink: 0 }}>
              <Upload size={18} color={T.orange} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: T.text }}>Banner uploaded</div>
              <a href={`${DOWNLOAD_BASE}${bannerUploaded.fileName}`} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 13, color: "#007bff", textDecoration: "underline", display: "block", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {bannerUploaded.fileName}
              </a>
            </div>
            <button onClick={clearBanner} title="Remove" style={{ background: "none", border: "none", color: T.danger, fontWeight: 800, fontSize: 16, cursor: "pointer", padding: "2px 7px" }}>✕</button>
          </div>
        ) : bannerUploading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 16px", background: T.bg, borderRadius: 10, border: `1.5px dashed ${T.border}` }}>
            <div style={{ width: 14, height: 14, border: `2px solid ${T.light}`, borderTop: `2px solid ${T.orange}`, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <span style={{ fontSize: 13, color: T.orange, fontWeight: 600 }}>Uploading…</span>
          </div>
        ) : (
          <div onClick={() => bannerRef.current?.click()}
            style={{ border: `2px dashed ${T.border}`, borderRadius: 10, padding: 24, textAlign: "center", cursor: "pointer", background: T.bg, transition: "border-color .2s" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = T.navy}
            onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
          >
            <div style={{ background: T.light, borderRadius: "50%", width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
              <Upload size={20} color={T.navy} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Click to upload banner</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>PNG, JPG · max 5 MB · 1200×400 px recommended</div>
          </div>
        )}
      </Field>
    </div>,

    // Step 1 — Location & Audience
    <div key="s1">
      <InfoBox>Set the physical or virtual location where the event will take place, and who it's for.</InfoBox>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Country" required>
          <SelectInput value={formData.country} onChange={e => handle("country", e.target.value)}>
            <option value="">Select country</option>
            {["India","UAE","Singapore","UK","USA","Australia","Canada","Malaysia","Kenya","South Africa"].map(c => <option key={c} value={c}>{c}</option>)}
          </SelectInput>
        </Field>
        <Field label="City" required>
          <Input value={formData.city} onChange={e => handle("city", e.target.value)} placeholder="e.g. Dubai" />
        </Field>
      </div>
      <Field label="Venue / Platform Details" required hint={formData.event_mode === "Online" ? "Name of the online platform (e.g. Zoom, MS Teams)" : "Full venue name and address"}>
        <TextareaInput value={formData.venue} onChange={e => handle("venue", e.target.value)} placeholder={formData.event_mode === "Online" ? "e.g. Zoom Webinar — hosted by Education Cell" : "Full venue name and address"} style={{ minHeight: 80 }} />
      </Field>
      <Field
        label={formData.event_mode === "Online" ? "Online Meeting Link" : formData.event_mode === "Hybrid" ? "Map Link / Online Meeting Link" : "Map Link"}
        hint="Optional — Google Maps link for the venue, or a meeting URL for online/hybrid events"
      >
        <Input value={formData.venue_link} onChange={e => handle("venue_link", e.target.value)} placeholder="https://..." />
      </Field>

      <Field label="Target Audience" required hint="Select all groups this event is intended for">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {TARGET_AUDIENCE_OPTIONS.map(opt => {
            const selectedList = (formData.target_audience || "").split(",").map(s => s.trim()).filter(Boolean);
            const selected = selectedList.includes(opt);
            return (
              <PillToggle key={opt} selected={selected} onClick={() => toggleTargetAudience(opt)}>
                {opt}
              </PillToggle>
            );
          })}
        </div>
      </Field>
    </div>,

    // Step 2 — Registration
    <div key="s2">
      <InfoBox>Set the window during which schools can submit their applications.</InfoBox>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Registration Start Date" required><Input type="date" value={formData.reg_open_date} onChange={e => handle("reg_open_date", e.target.value)} /></Field>
        <Field label="Registration Start Time" required><Input type="time" value={formData.reg_open_time} onChange={e => handle("reg_open_time", e.target.value)} /></Field>
        <Field label="Registration End Date" required><Input type="date" value={formData.reg_close_date} min={formData.reg_open_date || undefined} onChange={e => handle("reg_close_date", e.target.value)} /></Field>
        <Field label="Registration End Time" required><Input type="time" value={formData.reg_close_time} onChange={e => handle("reg_close_time", e.target.value)} /></Field>
      </div>
    </div>,

    // Step 3 — Review Setup
    <div key="s3">
      <InfoBox>The review period is when you evaluate applicant profiles before releasing invitations.</InfoBox>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Review Start Date" required><Input type="date" value={formData.review_start_date} onChange={e => handle("review_start_date", e.target.value)} /></Field>
        <Field label="Review End Date" required><Input type="date" value={formData.review_end_date} min={formData.review_start_date || undefined} onChange={e => handle("review_end_date", e.target.value)} /></Field>
      </div>
    </div>,

    // Step 4 — Invitation Setup
    <div key="s4">
      <InfoBox>Accepted schools must confirm participation before the acceptance deadline.</InfoBox>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Invitation Release Date" required><Input type="date" value={formData.invitation_release_date} onChange={e => handle("invitation_release_date", e.target.value)} /></Field>
        <Field label="Invitation Acceptance Deadline" required><Input type="date" value={formData.acceptance_deadline} min={formData.invitation_release_date || undefined} onChange={e => handle("acceptance_deadline", e.target.value)} /></Field>
      </div>
    </div>,

    // Step 5 — Agenda
    <div key="s5">
      <InfoBox>
        Add the event schedule. Every session date must fall between the event start
        ({formData.event_start_date || "not set"}) and end ({formData.event_end_date || "not set"}) dates,
        and each session's end time must be later than its start time.
        At least one valid session is required for event completion.
      </InfoBox>

      {(formData.agendaItems || []).length === 0 && (
        <div style={{ padding: 32, textAlign: "center", color: T.muted, fontSize: 14, border: `1.5px dashed ${T.border}`, borderRadius: 10, marginBottom: 16, background: T.bg }}>
          No agenda sessions yet. Add the first session of your event schedule.
        </div>
      )}

      {(formData.agendaItems || []).map((item, idx) => {
        const dateOutOfRange = !!item.item_date && (
          (formData.event_start_date && item.item_date < formData.event_start_date) ||
          (formData.event_end_date && item.item_date > formData.event_end_date)
        );
        const timeInvalid = !!item.start_time && !!item.end_time && item.start_time >= item.end_time;

        return (
          <div key={item.id} style={{ border: `1px solid ${T.border}`, borderRadius: 12, padding: 18, marginBottom: 16, background: T.bg }}>
            {/* Session header row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: T.navy, textTransform: "uppercase", letterSpacing: .5 }}>
                Session {idx + 1}
              </span>
              <button onClick={() => removeAgendaItem(item.id)}
                style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", color: T.danger, cursor: "pointer", fontSize: 15, fontWeight: 700, padding: 0 }}>
                <Trash2 size={14} /> Remove
              </button>
            </div>

            {/* ── Row 1: Title | Date | Start Time | End Time — all on one line ── */}
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 14, flexWrap: "nowrap" }}>
              {/* Title — takes remaining space */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ display: "block", fontSize: 15, fontWeight: 700, color: T.navy, marginBottom: 6, textTransform: "uppercase", letterSpacing: .3 }}>
                  Session Title
                </label>
                <Input
                  value={item.title}
                  onChange={e => updateAgendaItem(item.id, "title", e.target.value)}
                  placeholder="e.g. Opening Keynote"
                />
              </div>

              {/* Date — fixed narrow width */}
              <div style={{ width: 160, flexShrink: 0 }}>
                <label style={{ display: "block", fontSize: 15, fontWeight: 700, color: T.navy, marginBottom: 6, textTransform: "uppercase", letterSpacing: .3 }}>
                  Date <span style={{ color: T.danger }}>*</span>
                </label>
                <Input
                  type="date"
                  value={item.item_date}
                  min={formData.event_start_date || undefined}
                  max={formData.event_end_date || undefined}
                  onChange={e => updateAgendaItem(item.id, "item_date", e.target.value)}
                />
              </div>

              {/* Start Time — fixed narrow width */}
              <div style={{ width: 138, flexShrink: 0 }}>
                <label style={{ display: "block", fontSize: 15, fontWeight: 700, color: T.navy, marginBottom: 6, textTransform: "uppercase", letterSpacing: .3 }}>
                  Start <span style={{ color: T.danger }}>*</span>
                </label>
                <Input
                  type="time"
                  value={item.start_time}
                  onChange={e => updateAgendaItem(item.id, "start_time", e.target.value)}
                />
              </div>

              {/* End Time — fixed narrow width */}
              <div style={{ width: 138, flexShrink: 0 }}>
                <label style={{ display: "block", fontSize: 15, fontWeight: 700, color: T.navy, marginBottom: 6, textTransform: "uppercase", letterSpacing: .3 }}>
                  End <span style={{ color: T.danger }}>*</span>
                </label>
                <Input
                  type="time"
                  value={item.end_time}
                  min={item.start_time || undefined}
                  onChange={e => updateAgendaItem(item.id, "end_time", e.target.value)}
                />
              </div>
            </div>

            {/* ── Row 2: Description ── */}
            <div style={{ marginBottom: 4 }}>
              <label style={{ display: "block", fontSize: 15, fontWeight: 700, color: T.navy, marginBottom: 6, textTransform: "uppercase", letterSpacing: .3 }}>
                Description
              </label>
              <TextareaInput
                value={item.description || ""}
                onChange={e => updateAgendaItem(item.id, "description", e.target.value)}
                placeholder="Briefly describe the session — speaker, topic, format, etc."
                style={{ minHeight: 72 }}
              />
            </div>

            {/* Validation warnings */}
            {dateOutOfRange && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: T.danger, fontSize: 15, fontWeight: 700, marginTop: 8 }}>
                <AlertTriangle size={15} /> Session date must be within the event's start and end dates.
              </div>
            )}
            {timeInvalid && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: T.danger, fontSize: 15, fontWeight: 700, marginTop: 8 }}>
                <AlertTriangle size={15} /> End time must be later than the start time.
              </div>
            )}
          </div>
        );
      })}

      <Btn variant="outline" icon={Plus} onClick={addAgendaItem}>Add Agenda Session</Btn>
    </div>,

    // Step 6 — Participation Rules
    <div key="s6">
      <InfoBox>Define participation limits, collaboration details, and eligibility. These fields are optional.</InfoBox>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
        <Field label="Max Applicants" hint="Leave blank or 0 for unlimited">
          <Input type="number" min="0" value={formData.max_applicants} onChange={e => handle("max_applicants", e.target.value)} placeholder="e.g. 500" />
        </Field>
        <Field label="Max Invitations" hint="How many schools you will invite">
          <Input type="number" min="0" value={formData.max_invitations} onChange={e => handle("max_invitations", e.target.value)} placeholder="e.g. 120" />
        </Field>
        <Field label="Expected No. of Participants" hint="Estimated headcount">
          <Input type="number" min="0" value={formData.expected_participants} onChange={e => handle("expected_participants", e.target.value)} placeholder="e.g. 300" />
        </Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="International Collaboration Involved?">
          <SelectInput value={formData.international_collaboration} onChange={e => handle("international_collaboration", e.target.value)}>
            <option value="">Select</option>
            {YES_NO_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </SelectInput>
        </Field>
        <Field label="Are International Universities Invited?">
          <SelectInput value={formData.international_universities_invited} onChange={e => handle("international_universities_invited", e.target.value)}>
            <option value="">Select</option>
            {YES_NO_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </SelectInput>
        </Field>
      </div>

      <Field label="Eligibility Criteria">
        <TextareaInput value={formData.eligibility_criteria} onChange={e => handle("eligibility_criteria", e.target.value)} placeholder="Describe who can apply — school type, grade levels, location, etc." />
      </Field>

      {/* Brochure upload */}
      <Field label="Event Brochure" hint="PDF · max 5 MB">
        <input ref={brochureRef} type="file" accept=".pdf,application/pdf" style={{ display: "none" }} onChange={handleBrochureUpload} />
        {brochureUploaded ? (
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", background: "rgba(249,115,22,.05)", borderRadius: 10, border: `1.5px dashed ${T.orange}` }}>
            <div style={{ background: "rgba(249,115,22,.15)", borderRadius: 8, padding: 10, flexShrink: 0 }}>
              <FileText size={18} color={T.orange} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: T.text }}>Brochure uploaded</div>
              <a href={`${DOWNLOAD_BASE}${brochureUploaded.fileName}`} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 13, color: "#007bff", textDecoration: "underline", display: "block", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {brochureUploaded.fileName}
              </a>
            </div>
            <button onClick={clearBrochure} title="Remove" style={{ background: "none", border: "none", color: T.danger, fontWeight: 800, fontSize: 16, cursor: "pointer", padding: "2px 7px" }}>✕</button>
          </div>
        ) : brochureUploading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 16px", background: T.bg, borderRadius: 10, border: `1.5px dashed ${T.border}` }}>
            <div style={{ width: 14, height: 14, border: `2px solid ${T.light}`, borderTop: `2px solid ${T.orange}`, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <span style={{ fontSize: 13, color: T.orange, fontWeight: 600 }}>Uploading…</span>
          </div>
        ) : (
          <div onClick={() => brochureRef.current?.click()}
            style={{ border: `2px dashed ${T.border}`, borderRadius: 10, padding: 24, textAlign: "center", cursor: "pointer", background: T.bg, transition: "border-color .2s" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = T.navy}
            onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
          >
            <div style={{ background: T.light, borderRadius: "50%", width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
              <Upload size={20} color={T.navy} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Click to upload brochure</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>PDF · max 5 MB</div>
          </div>
        )}
      </Field>
    </div>,
  ];

  const completion   = calcCompletion(formData);
  const maxReachable = lastUnlockedStep(formData);

  // ════════════════════════════════════════════════════════════════
  // RENDER: LIST VIEW
  // ════════════════════════════════════════════════════════════════
  if (view === "list") {
    return (
      <div style={{ minHeight: "100vh", background: T.bg }}>
        <h2 className="pt-1 pb-1 text-center" style={{ color: "#0a2352", fontSize: "18px" }}>
          EVENT CREATION & MANAGEMENT
        </h2>

        <div style={{ margin: "0 auto", padding: "24px 32px 40px", boxSizing: "border-box" }}>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            <StatCard label="Total Events"       value={totalEvents}    icon={Calendar}  color={T.accent}  />
            <StatCard label="Registration Open"  value={activeEvents}   icon={Zap}       color={T.success} />
            <StatCard label="Published"          value={publishedCount} icon={FileText}  color={T.purple}  />
            <StatCard label="Drafts"             value={draftEvents}    icon={Edit3}     color={T.warn}    />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <div style={{ position: "relative", width: 290, paddingRight: "12px" }}>
              <Search size={15} color={T.muted} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
              <input
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Search events…"
                style={{ ...inputBase, paddingLeft: 34, fontSize: 14 }}
              />
            </div>
            <Btn variant="primary" icon={Plus} onClick={openCreate}>Create Event</Btn>
          </div>

          {listLoading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
              <div style={{ width: 40, height: 40, border: `4px solid ${T.light}`, borderTop: `4px solid ${T.orange}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          ) : (
            <Card style={{ overflow: "visible" }}>
              <div className="ag-theme-alpine" style={{ height: 520, width: "100%" }}>
                <AgGridReact
                  columnDefs={columnDefs}
                  rowData={events}
                  quickFilterText={searchText}
                  animateRows
                  pagination
                  paginationPageSize={12}
                  defaultColDef={{ sortable: true, filter: true, resizable: true }}
                  rowHeight={46}
                />
              </div>
            </Card>
          )}
        </div>

        {viewEvent    && <ViewModal event={viewEvent} onClose={() => setViewEvent(null)} />}
        {deleteTarget && (
          <ConfirmDialog
            message={`Are you sure you want to delete "${deleteTarget.event_name}"? This cannot be undone.`}
            onConfirm={confirmDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  // RENDER: FORM VIEW
  // ════════════════════════════════════════════════════════════════
  return (
    <div style={{ minHeight: "100vh", background: T.bg }}>
      <h2 className="pt-1 pb-1 text-center" style={{ color: "#0a2352", fontSize: "18px" }}>
        EVENT CREATION & MANAGEMENT
      </h2>

      <div style={{ margin: "0 auto", padding: "24px 32px 48px", boxSizing: "border-box" }}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 250px", gap: 24, alignItems: "start" }}>

          {/* ── Left: wizard ── */}
          <div>
            {/* Step tabs */}
            <div style={{ display: "flex", borderRadius: 12, overflow: "hidden", border: `1px solid ${T.border}`, marginBottom: 24, background: T.card }}>
              {STEPS.map((s, i) => {
                const isActive = step === i;
                const isDone   = stepComplete(i, formData);
                const isLocked = i > maxReachable + 1;
                const Icon     = s.icon;
                return (
                  <div key={i} onClick={() => tryGoToStep(i)}
                    style={{
                      flex: 1, padding: "14px 8px", minHeight: 82, textAlign: "center",
                      cursor: isLocked ? "not-allowed" : "pointer",
                      background: isActive
                        ? `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`
                        : isDone ? "rgba(22,163,74,.07)" : "transparent",
                      borderRight: i < STEPS.length - 1 ? `1px solid ${T.border}` : "none",
                      transition: "background .2s",
                      opacity: isLocked ? .45 : 1,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 5 }}>
                      {isDone
                        ? <Check size={16} color={isActive ? T.orange : T.success} />
                        : <Icon size={16} color={isActive ? T.orange : T.muted} />}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: isActive ? "#fff" : isDone ? T.success : T.muted, letterSpacing: .4 }}>{i + 1}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: isActive ? "rgba(255,255,255,.92)" : isDone ? T.success : T.muted, marginTop: 2, lineHeight: 1.25, whiteSpace: "normal" }}>{s.label}</div>
                    {isLocked && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>🔒</div>}
                  </div>
                );
              })}
            </div>

            {/* Step error banner */}
            {stepError && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16, padding: "12px 16px", background: "rgba(220,38,38,.07)", borderRadius: 8, border: `1px solid rgba(220,38,38,.25)` }}>
                <AlertTriangle size={16} color={T.danger} style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 15, color: T.danger, lineHeight: 1.5 }}>{stepError}</span>
                <button onClick={() => setStepError("")} style={{ marginLeft: "auto", background: "none", border: "none", color: T.danger, cursor: "pointer", padding: 0 }}><X size={15} /></button>
              </div>
            )}

            {/* Step card */}
            <Card>
              <div style={{ padding: "14px 22px", borderBottom: `1px solid ${T.border}`, background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ background: T.orange, borderRadius: 7, padding: "5px 7px", display: "flex", alignItems: "center" }}>
                  {React.createElement(STEPS[step].icon, { size: 16, color: "#fff" })}
                </div>
                <div>
                  <div style={{ color: "#fff", fontWeight: 800, fontSize: 17 }}>Step {step + 1} of {STEPS.length}: {STEPS[step].label}</div>
                  <div style={{ color: "rgba(255,255,255,.65)", fontSize: 14, marginTop: 2 }}>
                    {stepComplete(step, formData)
                      ? "✓ This step is complete"
                      : STEPS[step].label === "Agenda"
                        ? `${(formData.agendaItems || []).length} session(s) added — at least one valid session required`
                        : `${STEPS[step].fields.filter(f => !formData[f]).length} required field(s) remaining`}
                  </div>
                </div>
              </div>

              <div style={{ padding: 28 }}>{stepContent[step]}</div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 28px", borderTop: `1px solid ${T.border}`, background: T.bg }}>
                <Btn variant="outline" icon={ChevronLeft} onClick={() => tryGoToStep(step - 1)} disabled={step === 0}>Previous</Btn>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  {saveStatus === "saved" && (
                    <span style={{ display: "flex", alignItems: "center", gap: 5, color: T.success, fontWeight: 700, fontSize: 15 }}>
                      <CheckCircle size={15} /> Saved
                    </span>
                  )}
                  {saveStatus === "error" && (
                    <span style={{ display: "flex", alignItems: "center", gap: 5, color: T.danger, fontWeight: 700, fontSize: 15 }}>
                      <AlertCircle size={15} /> Save failed
                    </span>
                  )}
                  <Btn variant="outline" icon={Save} onClick={() => handleSave("draft")} disabled={saving} size="sm">
                    Save Draft
                  </Btn>
                  {step < STEPS.length - 1 ? (
                    <Btn variant="primary" onClick={() => tryGoToStep(step + 1)}>
                      Next <ChevronRight size={15} />
                    </Btn>
                  ) : (
                    <Btn variant="success" icon={Zap} onClick={() => handleSave("published")} disabled={saving}>
                      {saving ? "Publishing…" : "Publish Event"}
                    </Btn>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* ── Right sidebar ── */}
          <div style={{ position: "sticky", top: 76 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 10 }}>
              <Btn variant="outline" icon={List} onClick={goBackToList}
                style={{ background: `linear-gradient(135deg, ${T.navyDark} 0%, ${T.navy} 100%)`, color: "#fff", borderColor: "rgba(255,255,255,.4)" }}>
                Back to List
              </Btn>
            </div>

            <Card style={{ padding: 18, marginBottom: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: T.navy, textTransform: "uppercase", letterSpacing: .5, marginBottom: 10 }}>Overall Progress</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{ flex: 1, background: T.light, borderRadius: 99, height: 9, overflow: "hidden" }}>
                  <div style={{ width: `${completion}%`, height: "100%", borderRadius: 99, transition: "width .5s", background: completion === 100 ? T.success : T.orange }} />
                </div>
                <span style={{ fontSize: 15, fontWeight: 800, color: completion === 100 ? T.success : T.orange, minWidth: 36, textAlign: "right" }}>{completion}%</span>
              </div>
              <div style={{ fontSize: 14, color: T.muted }}>
                {ALL_REQUIRED_FIELDS.filter(f => formData[f]).length} of {ALL_REQUIRED_FIELDS.length} required fields filled
              </div>
              <div style={{ fontSize: 14, color: T.muted, marginTop: 4 }}>
                Agenda: {
                  Array.isArray(formData.agendaItems) && formData.agendaItems.length > 0 && formData.agendaItems.every(it => isAgendaItemValid(it, formData))
                    ? <span style={{ color: T.success, fontWeight: 700 }}>✓ Complete</span>
                    : <span style={{ color: T.warn, fontWeight: 700 }}>Pending</span>
                }
              </div>
            </Card>

            <Card style={{ padding: 18, marginBottom: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: T.navy, textTransform: "uppercase", letterSpacing: .5, marginBottom: 10 }}>Steps</div>
              {STEPS.map((s, i) => {
                const done   = stepComplete(i, formData);
                const locked = i > maxReachable + 1;
                const Icon   = s.icon;
                return (
                  <div key={i} onClick={() => !locked && tryGoToStep(i)}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", borderRadius: 7, marginBottom: 4, cursor: locked ? "not-allowed" : "pointer", background: step === i ? "rgba(10,35,82,.06)" : "transparent", border: step === i ? `1px solid ${T.border}` : "1px solid transparent", opacity: locked ? .5 : 1 }}
                  >
                    <div style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0, background: done ? T.success : step === i ? T.orange : T.light, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {done ? <Check size={13} color="#fff" /> : <Icon size={13} color={step === i ? "#fff" : T.muted} />}
                    </div>
                    <span style={{ fontSize: 15, fontWeight: step === i ? 700 : 500, color: step === i ? T.navy : T.muted, flex: 1, lineHeight: 1.25 }}>{s.label}</span>
                    {locked && <span style={{ fontSize: 12 }}>🔒</span>}
                    {done   && <span style={{ fontSize: 13, color: T.success, fontWeight: 700 }}>✓</span>}
                  </div>
                );
              })}
            </Card>

            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 14, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>Event Status</div>
              {statusBadge(formData.event_status || "draft")}
              <div style={{ marginTop: 10, fontSize: 15, color: T.muted, lineHeight: 1.6 }}>
                {formData.event_status === "draft"
                  ? "Save Draft keeps progress without publishing. Publish makes it live."
                  : formData.event_status === "published"
                  ? "Event is published and visible to schools."
                  : STATUS_META[formData.event_status]?.label || ""}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventManagement;